import { KanbanGroup, KanbanItem } from '@digiwall/goliath-core';
import { EnumerationTypeDto } from '@digiwall/core/proxy';
import { PlanningDataSource, PlanningGroupDto, PlanningItemDto } from '@digiwall/goliath-documents/proxy';
import { PlanningScaleBucket, toIsoDay } from './planning-scale';

/**
 * Payload carried on every kanban item/group. The board is generic, so each card keeps a reference to
 * its owning data source and dto; event handlers read it to route the intent back to the right adapter.
 */
export interface PlanningItemData {
  dataSource: PlanningDataSource;
  dto: PlanningItemDto;
  /**
   * Precomputed by the shell: is the item's desired (requested) date critical for its current
   * placement? Backlog → requestedDate ≤ today; planned → requestedDate ≤ the column's period start.
   * Drives the red (critical) vs grey styling of the "Souhaité" line on the card.
   */
  requestedDateCritical?: boolean;
}

export interface PlanningGroupData {
  dataSource: PlanningDataSource;
  dto: PlanningGroupDto;
}

const GROUP_MARKER = 'g';

/**
 * Kanban ids must be unique across data sources sharing one board, so every id is prefixed with its
 * data source. Guids never contain ':' and the enum values never contain ':', so the prefix is safe to
 * split on. Event handlers read the data source from `item.data`/`group.data`, not by parsing the id;
 * the prefix only guarantees uniqueness (and lets a drop target's group id resolve its source).
 */
export function itemKanbanId(dataSource: PlanningDataSource, entityId: string): string {
  return `${dataSource}:${entityId}`;
}

export function groupKanbanId(dataSource: PlanningDataSource, groupId: string): string {
  return `${dataSource}:${GROUP_MARKER}:${groupId}`;
}

/** The owning data source is the first ':'-delimited segment of any item or group kanban id. */
export function dataSourceOfKanbanId(id: string): PlanningDataSource {
  return id.split(':')[0] as PlanningDataSource;
}

/** Id of the column whose [start, end) window contains the date; null when the date is outside the view. */
export function bucketIdForDate(buckets: PlanningScaleBucket[], date: Date): string | null {
  const match = buckets.find(bucket => date >= bucket.start && date < bucket.end);
  return match ? match.id : null;
}

/** Local yyyy-MM-dd of a desired (requested) date, or null when absent — for chronological compares. */
function requestedIso(requestedDate: string | undefined): string | null {
  return requestedDate ? toIsoDay(new Date(requestedDate)) : null;
}

/**
 * True when the desired date is at/before the reference ISO day (yyyy-MM-dd) — i.e. critical.
 * Column ids and `toIsoDay(today)` are already yyyy-MM-dd, so the comparison is a plain string compare.
 */
export function requestedDateAtOrBefore(
  requestedDate: string | undefined,
  referenceIso: string,
): boolean {
  const iso = requestedIso(requestedDate);
  return iso !== null && iso <= referenceIso;
}

/** Ascending comparator for the backlog by desired date; items without one sort last. */
export function compareRequestedDate(a: string | undefined, b: string | undefined): number {
  const ai = requestedIso(a);
  const bi = requestedIso(b);
  if (ai === null && bi === null) {
    return 0;
  }
  if (ai === null) {
    return 1;
  }
  if (bi === null) {
    return -1;
  }
  return ai < bi ? -1 : ai > bi ? 1 : 0;
}

/**
 * Saturated accent colour of an enumeration value, used for the card's status dot and side border.
 * Prefers the readable `textColor` (a strong hue) over the pale `backgroundColor` tint so the accent
 * reads crisply, matching the kanban demo's coloured dot.
 */
export function enumAccentColor(entity: EnumerationTypeDto | undefined): string | undefined {
  return entity?.textColor ?? entity?.backgroundColor;
}

/** Accent colour of the item's status, resolved from the data source's status enumeration. */
export function statusColor(
  statuses: EnumerationTypeDto[],
  statusId: string | undefined,
): string | undefined {
  if (!statusId) {
    return undefined;
  }
  return enumAccentColor(statuses.find(entity => entity.id === statusId));
}

export function toKanbanItem(
  data: PlanningItemData,
  columnId: string | null,
  color: string | undefined,
): KanbanItem<PlanningItemData> {
  return {
    id: itemKanbanId(data.dataSource, data.dto.id!),
    columnId,
    groupId: data.dto.groupId ? groupKanbanId(data.dataSource, data.dto.groupId) : null,
    order: data.dto.order,
    statusColor: color,
    data,
  };
}

export function toKanbanGroup(
  data: PlanningGroupData,
  columnId: string,
): KanbanGroup<PlanningGroupData> {
  return {
    id: groupKanbanId(data.dataSource, data.dto.id!),
    columnId,
    order: data.dto.order,
    data,
  };
}
